import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { equipmentOptions } from 'functions/equipmentUtils'
import ExerciseCollection from 'components/ExerciseCollection/ExerciseCollection'

const INITIAL_MOUNT_Y_OFFSET = 25
const CARD_WIDTH = 260
const TITLE_HEIGHT = 24
const TITLE_GAP = 4
const PHASE_HEADER_HEIGHT = 20
const PHASE_HEADER_GAP = 10
const PHASE_GAP = 28
const SLOT_HEIGHT = 260
const SLOT_GAP = 16
const SLOT_SET_BLOCK_HEIGHT = 40
const COLLECTION_COLUMN_GAP = 160
const COLLECTION_CARD_WIDTH = 1000
const COLLECTION_ESTIMATED_HEIGHT = 280
const CONNECTOR_COLOR = '#a1a1aa'

function isJsonObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function formatOptionLabel(optionKey) {
    if (typeof optionKey !== 'string' || optionKey.length === 0) return 'Assessment'

    return optionKey.charAt(0).toUpperCase() + optionKey.slice(1)
}

function getAssessmentOptions(assessmentTemplatesJSON) {
    if (!isJsonObject(assessmentTemplatesJSON)) return []

    if (Array.isArray(assessmentTemplatesJSON.options)) {
        return assessmentTemplatesJSON.options.filter((optionKey) => (
            typeof optionKey === 'string' && optionKey.length > 0
        ))
    }

    return Object.keys(assessmentTemplatesJSON).filter((key) => (
        isJsonObject(assessmentTemplatesJSON[key]) && Array.isArray(assessmentTemplatesJSON[key].phases)
    ))
}

function formatSetDetails(set, exercise) {
    const setUnit = exercise?.setUnit || 'reps'
    const assigned = set?.setUnitsAssigned ?? 0
    const isUnilateral = exercise?.lateral === 'unilateral'
        || (exercise == null && Object.prototype.hasOwnProperty.call(set ?? {}, 'leftSetUnitsCompleted'))
    const unitsLabel = `${assigned} ${setUnit}${isUnilateral ? ' / side' : ''}`

    const weight = set?.labelWeightRecommendationRounded ?? set?.weightRecommendationRounded ?? 0
    let loadLabel = ''

    if (weight > 0) {
        loadLabel = `${weight} lbs`
    } else if (set?.formulaTag === 'bodyweight' || exercise?.exerciseType === 'bodyweight') {
        loadLabel = 'Bodyweight'
    } else if (set?.formulaTag) {
        loadLabel = set.formulaTag
    }

    return { unitsLabel, loadLabel }
}

function getEquipmentIcons(exercise) {
    if (!exercise?.equipment) return []

    return exercise.equipment
        .split(',')
        .map((key) => equipmentOptions.find((option) => option.key === key.trim()))
        .filter(Boolean)
}

function slotCardHeight(slot, collectionHeight = 0) {
    if (collectionHeight > 0) return collectionHeight

    const setCount = slot?.sets?.length ?? 0
    if (setCount <= 1) return SLOT_HEIGHT

    return SLOT_HEIGHT + (setCount - 1) * SLOT_SET_BLOCK_HEIGHT
}

function parseAssessmentApplications(value) {
    if (Array.isArray(value)) return value

    if (typeof value === 'string' && value.trim().length > 0) {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch {
            return []
        }
    }

    return []
}

function indexCollections(exerciseBlocks) {
    const byId = new Map()
    const byName = new Map()

    ;(exerciseBlocks ?? []).forEach((block) => {
        ;(block?.exerciseCollections ?? []).forEach((collection, index) => {
            if (!collection) return

            const entry = { collection, slotIndex: index }

            if (collection.exerciseCollectionId && !byId.has(collection.exerciseCollectionId)) {
                byId.set(collection.exerciseCollectionId, entry)
            }

            if (collection.name && !byName.has(collection.name)) {
                byName.set(collection.name, entry)
            }
        })
    })

    return { byId, byName }
}

function findMappedCollection(application, collectionIndex) {
    if (!application || !collectionIndex) return null

    if (application.exerciseCollectionId && collectionIndex.byId.has(application.exerciseCollectionId)) {
        return collectionIndex.byId.get(application.exerciseCollectionId)
    }

    if (application.exerciseCollectionName && collectionIndex.byName.has(application.exerciseCollectionName)) {
        return collectionIndex.byName.get(application.exerciseCollectionName)
    }

    return null
}

function findApplicationForSlot(applications, slot) {
    if (!slot) return null

    const byId = applications.find((application) => (
        application?.assessmentSlotId
        && application.assessmentSlotId === slot.assessmentSlotId
    ))

    if (byId) return byId

    if (!slot.assessmentSlotName) return null

    return applications.find((application) => (
        application?.assessmentSlotName
        && application.assessmentSlotName === slot.assessmentSlotName
    )) ?? null
}

function orthogonalConnectorPath(x1, y1, x2, y2) {
    const midX = x1 + (x2 - x1) / 2
    return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`
}

function buildAssessmentLayout(template, applications, collectionIndex, collectionHeights) {
    const items = []
    const connectors = []
    let y = 0
    let hasCollection = false

    items.push({
        type: 'title',
        key: 'title',
        y,
        height: TITLE_HEIGHT,
    })
    y += TITLE_HEIGHT + TITLE_GAP

    const phases = template?.phases ?? []
    phases.forEach((phase, phaseIndex) => {
        if (phaseIndex > 0) y += PHASE_GAP

        items.push({
            type: 'phase',
            key: `phase-${phaseIndex}`,
            y,
            height: PHASE_HEADER_HEIGHT,
            phase,
            phaseIndex,
        })
        y += PHASE_HEADER_HEIGHT + PHASE_HEADER_GAP

        ;(phase.slots ?? []).forEach((slot, slotIndex) => {
            if (slotIndex > 0) y += SLOT_GAP

            const key = `phase-${phaseIndex}-slot-${slotIndex}`
            const application = findApplicationForSlot(applications, slot)
            const mapped = findMappedCollection(application, collectionIndex)
            const collectionHeight = mapped
                ? (collectionHeights[key] ?? COLLECTION_ESTIMATED_HEIGHT)
                : 0
            const height = slotCardHeight(slot, collectionHeight)

            if (mapped) hasCollection = true

            items.push({
                type: 'slot',
                key,
                y,
                height,
                slot,
                slotIndex,
                phaseIndex,
                collection: mapped?.collection ?? null,
                collectionSlotIndex: mapped?.slotIndex ?? 0,
                collectionHeight,
            })

            if (mapped) {
                const x1 = CARD_WIDTH
                const y1 = y + height / 2
                const x2 = CARD_WIDTH + COLLECTION_COLUMN_GAP
                const y2 = y + height / 2

                connectors.push({
                    key,
                    path: orthogonalConnectorPath(x1, y1, x2, y2),
                    x1,
                    y1,
                    x2,
                    y2,
                })
            }

            y += height
        })
    })

    return {
        items,
        connectors,
        boardWidth: hasCollection
            ? CARD_WIDTH + COLLECTION_COLUMN_GAP + COLLECTION_CARD_WIDTH
            : CARD_WIDTH,
        boardHeight: Math.max(y, TITLE_HEIGHT),
    }
}

function AssessmentOptionDropdown({
    options = [],
    selectedOptionKey,
    onChange,
}) {
    return (
        <Listbox
            value={selectedOptionKey}
            onChange={onChange}
        >
            <div className="relative w-full">
                <Listbox.Button className="relative flex h-9 w-full cursor-pointer items-center rounded-md border border-zinc-200 bg-white/90 pl-3 pr-8 text-left text-sm text-zinc-700 shadow-sm backdrop-blur hover:bg-white focus:outline-none focus:ring-2 focus:ring-orange-400">
                    <span className="block truncate">
                        {selectedOptionKey ? formatOptionLabel(selectedOptionKey) : 'Select option'}
                    </span>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
                        <ChevronUpDownIcon className="h-5 w-5 text-zinc-400" />
                    </span>
                </Listbox.Button>

                <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-zinc-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                        {options.map((optionKey) => (
                            <Listbox.Option
                                key={optionKey}
                                value={optionKey}
                                className={({ active }) => `
                                    relative cursor-pointer select-none py-2 pl-3 pr-8
                                    ${active ? 'bg-orange-50' : ''}
                                `}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {formatOptionLabel(optionKey)}
                                        </span>

                                        {selected &&
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-orange-600">
                                                <CheckIcon className="h-5 w-5" />
                                            </span>
                                        }
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    )
}

function AssessmentCollectionCard({
    layoutKey,
    exerciseHashMap,
    programLevelHashMap,
    collection,
    collectionSlotIndex,
    onHeightChange,
}) {
    const rootRef = useRef(null)

    useLayoutEffect(() => {
        const element = rootRef.current
        if (!element) return

        const reportHeight = () => {
            onHeightChange(layoutKey, element.offsetHeight)
        }

        reportHeight()

        const resizeObserver = new ResizeObserver(reportHeight)
        resizeObserver.observe(element)

        return () => resizeObserver.disconnect()
    }, [layoutKey, collection, onHeightChange])

    return (
        <div
            ref={rootRef}
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md"
            onPointerDown={(event) => event.stopPropagation()}
        >
            <ExerciseCollection
                exerciseHashMap={exerciseHashMap}
                programLevelHashMap={programLevelHashMap}
                slotIndex={collectionSlotIndex}
                exerciseCollection={collection}
            />
        </div>
    )
}

function AssessmentCanvasBoard({
    template,
    exerciseHashMap = {},
    programLevelHashMap = {},
    layout,
    onCollectionHeightChange,
}) {
    const { items, connectors, boardWidth, boardHeight } = layout

    return (
        <div
            className="absolute left-0 top-0"
            style={{
                width: boardWidth,
                height: boardHeight,
            }}
        >
            {items.map((item) => {
                if (item.type === 'title') {
                    return null
                    // return (
                    //     <p
                    //         key={item.key}
                    //         className="absolute whitespace-nowrap text-left text-base font-medium leading-6 text-gray-900"
                    //         style={{
                    //             left: 0,
                    //             top: item.y,
                    //         }}
                    //     >
                    //         {template.name || 'Assessment'}
                    //     </p>
                    // )
                }

                if (item.type === 'phase') {
                    return null
                    // return (
                    //     <div
                    //         key={item.key}
                    //         className="absolute flex items-baseline justify-between"
                    //         style={{
                    //             left: 0,
                    //             top: item.y,
                    //             width: CARD_WIDTH,
                    //             height: item.height,
                    //         }}
                    //     >
                    //         <p className="text-left text-sm font-medium leading-5 text-gray-700">
                    //             {item.phase.name}
                    //         </p>

                    //         {item.phase.sets != null && (
                    //             <p className="shrink-0 text-xs text-gray-500">
                    //                 {item.phase.sets} {item.phase.sets === 1 ? 'set' : 'sets'}
                    //             </p>
                    //         )}
                    //     </div>
                    // )
                }

                const exercise = exerciseHashMap?.[item.slot.exerciseId] ?? null
                const sets = item.slot.sets ?? []
                const equipmentIcons = getEquipmentIcons(exercise)

                return (
                    <div
                        key={item.key}
                        className="absolute flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-md"
                        style={{
                            left: 0,
                            top: item.y,
                            width: CARD_WIDTH,
                            height: item.height,
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                    >
                        <div className="min-h-0 flex-1 overflow-hidden rounded-xl bg-gray-200">
                            {exercise?.imageUrl && (
                                <img
                                    src={exercise.imageUrl}
                                    alt={exercise.name || 'Exercise'}
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </div>

                        <div className="mt-3 shrink-0 space-y-1">
                            <p className="line-clamp-2 text-left text-sm font-medium leading-5 text-gray-900">
                                {exercise?.name || `Exercise ${item.slot.exerciseId}`}
                                <br/>
                                <span className="text-xs text-gray-500">{exercise?.category}</span>
                            </p>

                            {equipmentIcons.length > 0 && (
                                <div className="flex items-center gap-x-1">
                                    {equipmentIcons.map((equipment) => (
                                        <img
                                            key={equipment.key}
                                            src={equipment.imageURL}
                                            alt={equipment.name || 'Equipment'}
                                            className="h-5"
                                        />
                                    ))}
                                </div>
                            )}

                            {sets.map((set, setIndex) => {
                                const { unitsLabel, loadLabel } = formatSetDetails(set, exercise)

                                return (
                                    <div key={setIndex} className="text-sm leading-5 text-gray-500">
                                        {sets.length > 1 && (
                                            <p className="text-xs text-gray-400">Set {setIndex + 1}</p>
                                        )}
                                        <p>{unitsLabel}</p>
                                        {loadLabel && <p>{loadLabel}</p>}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )
            })}

            {items.map((item) => {
                if (item.type !== 'slot' || !item.collection) return null

                return (
                    <div
                        key={`${item.key}-collection`}
                        className="absolute"
                        style={{
                            left: CARD_WIDTH + COLLECTION_COLUMN_GAP,
                            top: item.y,
                            width: COLLECTION_CARD_WIDTH,
                        }}
                    >
                        <AssessmentCollectionCard
                            layoutKey={item.key}
                            exerciseHashMap={exerciseHashMap}
                            programLevelHashMap={programLevelHashMap}
                            collection={item.collection}
                            collectionSlotIndex={item.collectionSlotIndex}
                            onHeightChange={onCollectionHeightChange}
                        />
                    </div>
                )
            })}

            <svg
                className="pointer-events-none absolute left-0 top-0 z-10 overflow-visible"
                width={boardWidth}
                height={boardHeight}
                aria-hidden="true"
            >
                {connectors.map((connector) => (
                    <g key={connector.key}>
                        <path
                            d={connector.path}
                            fill="none"
                            stroke={CONNECTOR_COLOR}
                            strokeWidth="1.5"
                        />
                        <circle
                            cx={connector.x1}
                            cy={connector.y1}
                            r="3.5"
                            fill="#f4f4f5"
                            stroke={CONNECTOR_COLOR}
                            strokeWidth="1.5"
                        />
                        <circle
                            cx={connector.x2}
                            cy={connector.y2}
                            r="3.5"
                            fill="#f4f4f5"
                            stroke={CONNECTOR_COLOR}
                            strokeWidth="1.5"
                        />
                    </g>
                ))}
            </svg>
        </div>
    )
}

export default function AssessmentCanvasTab({
    viewportRef,
    setTransform = () => {},
    exerciseHashMap = {},
    programLevelHashMap = {},
    assessmentTemplatesJSON = null,
    assessmentApplicationJSON = [],
    exerciseBlocks = [],
}) {
    const options = getAssessmentOptions(assessmentTemplatesJSON)
    const [selectedOptionKey, setSelectedOptionKey] = useState(() => options[0] ?? null)
    const [portalElement, setPortalElement] = useState(null)
    const [collectionHeights, setCollectionHeights] = useState({})
    const applications = useMemo(
        () => parseAssessmentApplications(assessmentApplicationJSON),
        [assessmentApplicationJSON],
    )
    const collectionIndex = useMemo(
        () => indexCollections(exerciseBlocks),
        [exerciseBlocks],
    )

    const onCollectionHeightChange = useCallback((layoutKey, height) => {
        setCollectionHeights((current) => {
            const optionHeights = current[selectedOptionKey] ?? {}
            if (optionHeights[layoutKey] === height) return current

            return {
                ...current,
                [selectedOptionKey]: {
                    ...optionHeights,
                    [layoutKey]: height,
                },
            }
        })
    }, [selectedOptionKey])

    useEffect(() => {
        const nextOptions = getAssessmentOptions(assessmentTemplatesJSON)
        const hasSelectedOption = nextOptions.includes(selectedOptionKey)

        if (!hasSelectedOption) {
            setSelectedOptionKey(nextOptions[0] ?? null)
        }
    }, [assessmentTemplatesJSON, selectedOptionKey])

    useLayoutEffect(() => {
        setPortalElement(viewportRef?.current ?? null)
    }, [viewportRef])

    const selectedTemplate = isJsonObject(assessmentTemplatesJSON?.[selectedOptionKey])
        ? assessmentTemplatesJSON[selectedOptionKey]
        : null

    const layout = useMemo(() => {
        if (!isJsonObject(selectedTemplate)) return null

        return buildAssessmentLayout(
            selectedTemplate,
            applications,
            collectionIndex,
            collectionHeights[selectedOptionKey] ?? {},
        )
    }, [selectedTemplate, applications, collectionIndex, collectionHeights, selectedOptionKey])

    useEffect(() => {
        if (!isJsonObject(selectedTemplate)) return

        const viewport = viewportRef?.current
        if (!viewport) return

        const { boardWidth, boardHeight } = buildAssessmentLayout(
            selectedTemplate,
            applications,
            collectionIndex,
            {},
        )
        const { width, height } = viewport.getBoundingClientRect()
        const focusWidth = boardWidth > CARD_WIDTH
            ? CARD_WIDTH + COLLECTION_COLUMN_GAP / 2
            : boardWidth
        const topClearance = 108

        setTransform({
            x: Math.max(48, width / 2 - focusWidth / 2),
            y: Math.max(topClearance, height / 2 - boardHeight / 2 + INITIAL_MOUNT_Y_OFFSET),
            zoom: 1,
        })
    }, [selectedTemplate, applications, collectionIndex, viewportRef, setTransform])

    const optionDropdown = portalElement && options.length > 1 && createPortal(
        <div
            className="absolute top-[4.5rem] left-1/2 z-50 w-40 -translate-x-1/2"
            onPointerDown={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
        >
            <AssessmentOptionDropdown
                options={options}
                selectedOptionKey={selectedOptionKey}
                onChange={setSelectedOptionKey}
            />
        </div>,
        portalElement,
    )

    return (
        <>
            {optionDropdown}

            {selectedTemplate && layout && (
                <AssessmentCanvasBoard
                    template={selectedTemplate}
                    exerciseHashMap={exerciseHashMap}
                    programLevelHashMap={programLevelHashMap}
                    layout={layout}
                    onCollectionHeightChange={onCollectionHeightChange}
                />
            )}
        </>
    )
}
